import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { connectRefinementList } from 'react-instantsearch-dom';
import styled from 'styled-components';
import { ReactComponent as CheckIcon } from '../../../assets/images/svg/check-circle-blue.svg';
import themeColors from '../../../assets/styles/themeColors';
import { variables } from '../../../assets/styles/variables';
import { mediaBelow } from '../../../assets/styles/helper';

const Chips = styled.div`
  display: flex;
  gap: 8px;
`;

const Chip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 6px;
  border: 1px solid
    ${({ $active }) =>
      $active ? themeColors.colorPrimary : themeColors.colorBorderPrimary};
  background: ${themeColors.colorWhite};
  color: ${({ $active }) =>
    $active ? themeColors.colorPrimary : themeColors.colorTextSecondary};
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 16px;
  font-weight: 500;
  line-height: 18px;
  cursor: pointer;

  > svg {
    width: 16px;
    height: 16px;
  }
  ${mediaBelow(variables.breakpoints.bpMd)} {
    font-size: 14px;
    line-height: 18px;
    font-weight: 500;
  }
`;

// MetalType has two representations on this codebase: the string label
// ("Gold"/"Silver") and the numeric API enum (Gold=1, Silver=2 — see
// productMetalTypes in src/util/enum/api/productTypes.js and the parseEnumType
// helper). The Sell index can contain a mix, so refine by BOTH variants under
// an OR operator and recognise either when checking active state.
const METAL_VARIANTS = {
  Gold: ['gold', '1'],
  Silver: ['silver', '2'],
};

// In react-instantsearch-dom v6, `item.label` is the actual facet value
// returned by Algolia (e.g. "Gold" or "1"). `item.value` is NOT the facet
// value — it is the array representing the next refinement state if that
// item were toggled. So always refine by labels, never by value.
const matchesMetal = (item, label) => {
  const variants = METAL_VARIANTS[label];
  const itemLabel = String(item?.label ?? '')
    .trim()
    .toLowerCase();
  return variants.includes(itemLabel);
};

const isMetalRefined = (items, label) =>
  items.some((item) => item.isRefined && matchesMetal(item, label));

const refineMetal = (items, refine, label) => {
  const variants = METAL_VARIANTS[label];
  // Use whichever variants of this metal Algolia is currently exposing as
  // facet values; fall back to both canonical variants if facets haven't
  // loaded yet (initial render).
  const presentLabels = items
    .filter((item) => matchesMetal(item, label))
    .map((item) => item.label);
  refine(presentLabels.length ? presentLabels : variants);
};

const SellFromHomeMetalChips = ({ items, refine, onRefine }) => {
  const { t } = useTranslation();

  const goldRefined = isMetalRefined(items, 'Gold');
  const silverRefined = isMetalRefined(items, 'Silver');
  const isAnyRefined = items.some((item) => item.isRefined);

  const handleRefine = (nextValue) => {
    if (onRefine) {
      onRefine();
    }
    refine(nextValue);
  };

  return (
    <Chips>
      <Chip
        type="button"
        $active={!isAnyRefined}
        onClick={() => handleRefine([])}
      >
        {t('sellWizard.sfhWizard.filterAll')}
        {!isAnyRefined && <CheckIcon />}
      </Chip>
      <Chip
        type="button"
        $active={goldRefined}
        onClick={() =>
          refineMetal(items, (value) => handleRefine(value), 'Gold')
        }
      >
        {t('sellWizard.sfhWizard.filterGold')}
        {goldRefined && <CheckIcon />}
      </Chip>
      <Chip
        type="button"
        $active={silverRefined}
        onClick={() =>
          refineMetal(items, (value) => handleRefine(value), 'Silver')
        }
      >
        {t('sellWizard.sfhWizard.filterSilver')}
        {silverRefined && <CheckIcon />}
      </Chip>
    </Chips>
  );
};

SellFromHomeMetalChips.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  refine: PropTypes.func.isRequired,
  onRefine: PropTypes.func,
};

export default connectRefinementList(SellFromHomeMetalChips);
